import { Box, Group, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { tokens } from '../tokens.ts';
import type { NavGroup, NavItem, NavLeaf } from './nav-tree.tsx';
import { pathMatches, pickActiveLeafPath } from './nav-tree.tsx';
import type { SidebarFoldState } from './use-sidebar-fold-state.ts';
import { useSidebarKeyboardNav } from './use-sidebar-keyboard-nav.ts';

/**
 * Presentational sidebar — renders the nav tree with role-filtered
 * visibility. Zero state; zero persistence; zero knowledge of role or
 * profile. Consumers pass the already-filtered `tree`, the `fold`
 * handle from useSidebarFoldState, and `currentPath`.
 *
 * Keyboard nav (WAI-ARIA tree pattern):
 *   ↓/↑   walk siblings + open children
 *   →     expand collapsed group / focus first child of open group
 *   ←     focus parent group / collapse open group
 *   Enter / Space   activate (NavLink for leaves; toggle for groups)
 *   Home / End      first / last
 *
 * `activeLeafTo` — picked once per render via longest-prefix match.
 * Avoids the multi-highlight bug where /payments + /payments/charges
 * both lit up (regression 2026-04-22).
 */
export function Sidebar({
  tree,
  fold,
  currentPath,
  onNavigate,
}: {
  tree: readonly NavItem[];
  fold: SidebarFoldState;
  currentPath: string;
  onNavigate?: () => void;
}): JSX.Element {
  const activeLeafTo = pickActiveLeafPath(tree, currentPath);
  const kb = useSidebarKeyboardNav(tree, fold.isOpen, fold.toggle);

  return (
    <div role="tree" aria-label="主要導覽">
      {tree.map((n) =>
        n.type === 'leaf' ? (
          <LeafRow
            key={n.to}
            leaf={n}
            activeLeafTo={activeLeafTo}
            onNavigate={onNavigate}
            isFocused={kb.focusedKey === n.to || (kb.focusedKey === null && n.to === activeLeafTo)}
            onKeyDown={(e) => kb.handleKeyDown(e, n.to)}
            onFocus={() => kb.setFocusedKey(n.to)}
            registerNode={kb.registerNode(n.to)}
          />
        ) : (
          <GroupRow
            key={n.key}
            group={n}
            open={fold.isOpen(n.key)}
            onToggle={() => fold.toggle(n.key)}
            currentPath={currentPath}
            activeLeafTo={activeLeafTo}
            onNavigate={onNavigate}
            isFocused={kb.focusedKey === n.key}
            onKeyDown={(e) => kb.handleKeyDown(e, n.key)}
            onFocus={() => kb.setFocusedKey(n.key)}
            registerNode={kb.registerNode}
            kbHandleKeyDown={kb.handleKeyDown}
            kbSetFocusedKey={kb.setFocusedKey}
            kbFocusedKey={kb.focusedKey}
          />
        ),
      )}
    </div>
  );
}

function LeafRow({
  leaf,
  activeLeafTo,
  nested = false,
  onNavigate,
  isFocused,
  onKeyDown,
  onFocus,
  registerNode,
}: {
  leaf: NavLeaf;
  activeLeafTo: string | null;
  nested?: boolean;
  onNavigate?: () => void;
  isFocused: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus: () => void;
  registerNode: (node: HTMLElement | null) => void;
}): JSX.Element {
  const active = leaf.to === activeLeafTo;
  return (
    <RouterNavLink
      to={leaf.to}
      end={leaf.to === '/'}
      style={{ textDecoration: 'none' }}
      onClick={onNavigate}
      role="treeitem"
      aria-selected={active}
      tabIndex={isFocused ? 0 : -1}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      ref={registerNode}
    >
      <Group
        gap="sm"
        px="md"
        py="xs"
        wrap="nowrap"
        style={{
          borderRadius: tokens.radius.md,
          marginBottom: 2,
          marginLeft: nested ? 16 : 0,
          background: active ? tokens.color.navbarItemActiveBg : 'transparent',
          color: active ? tokens.color.navbarItemActive : tokens.color.navbarItem,
          fontWeight: active ? 600 : 500,
          transition: 'background 120ms ease',
        }}
      >
        {leaf.icon(nested ? 16 : 18)}
        <Text size={nested ? 'sm' : 'md'}>{leaf.label}</Text>
      </Group>
    </RouterNavLink>
  );
}

function GroupRow({
  group,
  open,
  onToggle,
  currentPath,
  activeLeafTo,
  onNavigate,
  isFocused,
  onKeyDown,
  onFocus,
  registerNode,
  kbHandleKeyDown,
  kbSetFocusedKey,
  kbFocusedKey,
}: {
  group: NavGroup;
  open: boolean;
  onToggle: () => void;
  currentPath: string;
  activeLeafTo: string | null;
  onNavigate?: () => void;
  isFocused: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus: () => void;
  registerNode: (key: string) => (node: HTMLElement | null) => void;
  kbHandleKeyDown: (e: React.KeyboardEvent, key: string) => void;
  kbSetFocusedKey: (key: string | null) => void;
  kbFocusedKey: string | null;
}): JSX.Element {
  const hasActiveChild = group.children.some((c) => pathMatches(c.to, currentPath));
  return (
    <Box mb={4}>
      <Group
        gap="sm"
        px="md"
        py="xs"
        wrap="nowrap"
        role="treeitem"
        aria-expanded={open}
        tabIndex={isFocused ? 0 : -1}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
            return;
          }
          onKeyDown(e);
        }}
        onFocus={onFocus}
        ref={registerNode(group.key)}
        style={{
          cursor: 'pointer',
          borderRadius: tokens.radius.md,
          background: hasActiveChild && !open ? tokens.color.navbarItemActiveBg : 'transparent',
          color: hasActiveChild ? tokens.color.navbarItemActive : tokens.color.navbarItem,
          fontWeight: 600,
          transition: 'background 120ms ease',
          outline: 'none',
        }}
      >
        {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        {group.icon(18)}
        <Text size="md" style={{ flex: 1 }}>
          {group.label}
        </Text>
      </Group>
      {open && (
        <Box mt={2} role="group">
          {group.children.map((c) => (
            <LeafRow
              key={c.to}
              leaf={c}
              activeLeafTo={activeLeafTo}
              nested
              onNavigate={onNavigate}
              isFocused={kbFocusedKey === c.to}
              onKeyDown={(e) => kbHandleKeyDown(e, c.to)}
              onFocus={() => kbSetFocusedKey(c.to)}
              registerNode={registerNode(c.to)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
